import { useContext } from 'react';
import L, { LatLngExpression } from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import '@changey/react-leaflet-markercluster/dist/styles.min.css';
import { AppContext } from 'providers/AppProvider';

interface VisitorsMapProps {
  visitors: any[];
}

// Jitter overlapping points slightly so multiple visitors from the same city
// don't stack into one dot.
const jitter = (n: number, i: number) => n + ((i % 7) - 3) * 0.06;

const VisitorsMap = ({ visitors }: VisitorsMapProps) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const located = visitors.filter(
    v => typeof v.lat === 'number' && typeof v.lng === 'number' && isFinite(v.lat) && isFinite(v.lng)
  );

  const center: LatLngExpression = [20, 0];
  const tileUrl = isDark
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  return (
    <div style={{ position: 'relative' }}>
      {located.length === 0 && (
        <div className="text-body-tertiary fs-9 mb-2">
          No located visitors yet — locations appear once visitors connect with the
          latest widget.
        </div>
      )}
      <MapContainer
        center={center}
        zoom={2}
        minZoom={2}
        scrollWheelZoom
        style={{ height: 460, borderRadius: 12, zIndex: 0 }}
        worldCopyJump
      >
        <TileLayer url={tileUrl} attribution="&copy; OpenStreetMap, &copy; CARTO" />
        <MarkerClusterGroup chunkedLoading={true} spiderfyOnMaxZoom>
          {located.map((v, i) => (
            <CircleMarker
              key={v.customer_id || i}
              center={[jitter(v.lat, i), jitter(v.lng, i)]}
              radius={7}
              pathOptions={{
                color: v.active ? '#25b865' : '#98a2b3',
                fillColor: v.active ? '#25b865' : '#98a2b3',
                fillOpacity: 0.7,
                weight: 2
              }}
            >
              <Popup>
                <div style={{ minWidth: 140 }}>
                  <strong>{v.name || 'Visitor'}</strong>
                  <div className="text-body-tertiary">
                    {[v.city, v.region, v.country_name || (v.country || '').toUpperCase()]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                  <div className="text-body-tertiary">{v.page || ''}</div>
                  <div style={{ marginTop: 4 }}>
                    {v.chat_started ? '💬 In chat' : v.active ? '🟢 Active' : '⚪ Offline'}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default VisitorsMap;
